import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
import { exportQuotePdf } from '../utils/quotePdfExport';
import { exportQuoteExcel } from '../utils/quoteExcelExport';
// 需求 R168：模板二「Excel 預購表用」（15 欄），與標準版 Excel／PDF 並列第三個匯出選項。
import { exportQuotePreorderExcel } from '../utils/quotePreorderExcelExport';
// 需求 R132 R5（PM 2026-08-27 追問「其他幣別是否也會發生兩次進位」）：
// 全幣別盤點時查到本頁的單價／小計是寫死的 toFixed(2)，**完全不看幣別** —— 與
// 需求 R111 當年在匯出層查到的是同一類缺陷（那次修了 PDF/Excel，漏了詳情頁）。
// 後果：TWD／HKD 報價單在此印成 60.90（規則是 1 位 → 60.9）；且 toFixed 是四捨五入，
// 手動單價存了 3 位小數時（如 USD 2.344）詳情頁印 2.34、匯出印 2.35，兩處對不起來。
// 改法：一律走 quoteMoney.js 的 ceilMoney + moneyDecimals（USD／EUR 2 位、TWD／HKD 1 位），
// 與畫面 formatPrice、匯出 fmtMoney 同一套規則。存值本身已進位過，再套一次是等冪的，
// 不會變成「第二次進位」（見 tests/quoteCurrencyRoundingAudit.fixture.js [4]）。
// 需求 R140 Q2=A（PM 2026-08-28 逐字：「已存檔的非台幣舊報價單要不要主動
// 回寫成新公式？ A＝不動，維持『開編輯頁重存才更新』」）：
// 本頁的單價／小計一律**讀 DB 存值 item.unitPrice，不重算**。已知且經 PM 拍板接受的落差：
// 需求 R132（2026-08-27，b981707）之前存檔的報價單，其 unitPrice 是舊公式（成本被進位兩次）
// 算出來的值，四種幣別都可能比現行公式高一個進位單位（TWD/HKD 0.1、USD/EUR 0.01）。
// 這類舊單要等使用者開啟編輯頁重新存檔（需求 R104 Q3=B「歷史單開啟即以現行公式重算」）
// 才會落地成新值；本頁與 PDF／Excel 三處都不做即時重算、也不做批次回寫。
// → 後人若想在此加「顯示時重算」，請先回 需求 R140 Q2 確認 PM 是否改判（現為 A）。
import { ceilMoney, moneyDecimals } from '../utils/quoteMoney';
import './QuoteDetailPage.css';
const API = process.env.REACT_APP_API_URL || '/api';

/** 依幣別位數輸出金額字串（無值回 '—'）。不加千分位，維持本頁原本的純數字外觀。 */
function fmtDetailMoney(value, currency) {
  const ceiled = ceilMoney(value, currency);
  if (ceiled === null) return '—';
  return ceiled.toFixed(moneyDecimals(currency));
}

const STATUS_MAP = {
  draft: { label: '草稿', className: 'qd-status-draft' },
  pending: { label: '審核中', className: 'qd-status-pending' },
  approved: { label: '已審核', className: 'qd-status-approved' },
  rejected: { label: '退回修改', className: 'qd-status-rejected' },
};

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('zh-TW');
}

const QuoteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Cancel confirmation modal
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Export menu
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);

  // Auth
  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!t || !u._id) { navigate('/login'); return; }
    setToken(t);
    setUser(u);
  }, [navigate]);

  // Fetch quote
  const fetchQuote = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setQuote(data);
    } catch (err) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Close export menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Status transition
  const handleStatusChange = async (newStatus) => {
    setActionLoading(newStatus);
    setError('');
    try {
      const res = await fetch(`${API}/quotes/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || '操作失敗');
      }
      await fetchQuote();
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading('');
    }
  };

  // Navigate back / cancel
  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    navigate('/quotes');
  };

  // Mark quote as exported
  const markAsExported = async () => {
    try {
      await fetch(`${API}/quotes/${id}/exported`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // Silently ignore — export itself succeeded
    }
  };

  // Export to Excel — 需求 R14：改用 exportQuoteExcel（ExcelJS），版面與 PDF 完全對齊
  const handleExportExcel = async () => {
    setShowExportMenu(false);
    try {
      await exportQuoteExcel({ quote });
      await markAsExported();
    } catch (err) {
      setError('Excel 輸出失敗: ' + err.message);
    }
  };

  const handleExportPDF = async () => {
    setShowExportMenu(false);
    try {
      await exportQuotePdf({ quote });
      await markAsExported();
    } catch (err) {
      setError('PDF 輸出失敗: ' + err.message);
    }
  };

  // 需求 R168（PM 2026-09-12 Q7=A）：預購表匯出成功同樣寫「已列印/匯出」旗標，比照現行兩種匯出。
  const handleExportPreorderExcel = async () => {
    setShowExportMenu(false);
    try {
      await exportQuotePreorderExcel({ quote });
      await markAsExported();
    } catch (err) {
      setError('Excel 預購表輸出失敗: ' + err.message);
    }
  };

  // Determine what buttons to show based on role + status
  const getActionButtons = () => {
    if (!quote || !user) return null;
    const status = quote.status;
    const role = user.role;
    const buttons = [];

    // Cancel button (always, goes back to list)
    buttons.push(
      <button
        key="cancel"
        className="qd-btn qd-btn-secondary"
        onClick={handleCancel}
      >
        取消
      </button>
    );

    if (status === 'draft' || status === 'rejected') {
      // Sales/Manager can edit draft or rejected quotes
      if (role === 'sales' || role === 'manager') {
        buttons.push(
          <button
            key="save-draft"
            className="qd-btn qd-btn-secondary"
            onClick={() => navigate(`/quotes/${id}/edit`)}
          >
            編輯草稿
          </button>
        );
        buttons.push(
          <button
            key="submit"
            className="qd-btn qd-btn-primary"
            onClick={() => handleStatusChange('pending')}
            disabled={!!actionLoading}
          >
            {actionLoading === 'pending' ? '送出中…' : '送出審核'}
          </button>
        );
      }
    }

    if (status === 'pending') {
      // Sales view: show disabled "待審核" button
      if (role === 'sales') {
        buttons.push(
          <button key="pending-sales" className="qd-btn qd-btn-pending-disabled" disabled>
            待審核
          </button>
        );
      }
      // Purchasing view: show disabled "待審核" button
      if (role === 'purchasing') {
        buttons.push(
          <button key="pending-purchasing" className="qd-btn qd-btn-pending-disabled" disabled>
            待審核
          </button>
        );
      }
      // Manager view: edit + approve + reject
      if (role === 'manager') {
        buttons.push(
          <button
            key="edit-pending"
            className="qd-btn qd-btn-secondary"
            onClick={() => navigate(`/quotes/${id}/edit`)}
          >
            編輯
          </button>
        );
        buttons.push(
          <button
            key="approve"
            className="qd-btn qd-btn-success"
            onClick={() => handleStatusChange('approved')}
            disabled={!!actionLoading}
          >
            {actionLoading === 'approved' ? '審核中…' : '審核確認'}
          </button>
        );
        buttons.push(
          <button
            key="reject"
            className="qd-btn qd-btn-danger"
            onClick={() => handleStatusChange('rejected')}
            disabled={!!actionLoading}
          >
            {actionLoading === 'rejected' ? '退回中…' : '審核退回'}
          </button>
        );
      }
    }

    if (status === 'approved') {
      buttons.push(
        <button key="approved-badge" className="qd-btn qd-btn-approved-disabled" disabled>
          已審核
        </button>
      );
    }

    // Print button: available when pending or approved
    if (status === 'pending' || status === 'approved') {
      buttons.push(
        <div key="print" className="qd-export-wrapper" ref={exportRef}>
          <button
            className="qd-btn qd-btn-info"
            onClick={() => setShowExportMenu(!showExportMenu)}
          >
            列印 / 匯出
          </button>
          {showExportMenu && (
            <div className="qd-export-menu">
              {/* 需求 R168 Q4=A：三顆、順序 PDF → Excel 標準 → Excel 預購表，文案照描述；編輯頁 .qc-export-menu 同步 */}
              <button className="qd-export-option" onClick={handleExportPDF}>
                PDF 標準報價單
              </button>
              <button className="qd-export-option" onClick={handleExportExcel}>
                Excel 標準報價單
              </button>
              <button className="qd-export-option" onClick={handleExportPreorderExcel}>
                Excel 預購表用
              </button>
            </div>
          )}
        </div>
      );
    }

    return buttons;
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="qd-page">
        <Header title="報價單詳情" user={user} onLogout={handleLogout} onUserUpdate={setUser} />
        <div className="qd-container">
          <div className="qd-loading">載入中…</div>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="qd-page">
        <Header title="報價單詳情" user={user} onLogout={handleLogout} onUserUpdate={setUser} />
        <div className="qd-container">
          <div className="qd-error-banner">{error || '找不到此報價單'}</div>
          <button className="qd-btn qd-btn-secondary" onClick={() => navigate('/quotes')}>回到列表</button>
        </div>
      </div>
    );
  }

  const st = STATUS_MAP[quote.status] || STATUS_MAP.draft;

  return (
    <div className="qd-page">
      <Header title="報價單詳情" user={user} onLogout={handleLogout} onUserUpdate={setUser} />

      <div className="qd-container">
        {error && <div className="qd-error-banner">{error}</div>}

        {/* Status + Last Edited */}
        <div className="qd-top-bar">
          <div className="qd-top-left">
            <span className="qd-quote-number">{quote.quoteNumber}</span>
            <span className={`qd-status-badge ${st.className}`}>{st.label}</span>
          </div>
          <div className="qd-top-right">
            <span className="qd-last-edited">
              最後編輯：{formatDateTime(quote.updatedAt)}
            </span>
          </div>
        </div>

        {/* Section 1: Basic Info */}
        <section className="qd-section">
          <div className="qd-section-header">
            <span className="qd-section-number">1</span>
            報價單基本資訊
          </div>
          <div className="qd-section-body">
            <div className="qd-info-grid">
              <div className="qd-info-item">
                <span className="qd-info-label">報價單編號</span>
                <span className="qd-info-value qd-mono">{quote.quoteNumber}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">報價日期</span>
                <span className="qd-info-value">{formatDate(quote.quoteDate)}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">截止日期</span>
                <span className="qd-info-value">{formatDate(quote.expiryDate)}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">公司抬頭</span>
                <span className="qd-info-value">{quote.companyHeader}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">客戶名稱</span>
                <span className="qd-info-value">{quote.customerName || quote.customer?.name || '—'}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">報價幣別</span>
                <span className="qd-info-value">{quote.currency}</span>
              </div>
              {quote.currency !== 'TWD' && (
                <div className="qd-info-item">
                  <span className="qd-info-label">匯率</span>
                  <span className="qd-info-value">{quote.exchangeRate}</span>
                </div>
              )}
              {quote.specialFormula && (
                <div className="qd-info-item">
                  <span className="qd-info-label">特殊公式</span>
                  <span className="qd-info-value qd-mono">{quote.specialFormula}</span>
                </div>
              )}
              {quote.globalMarkup > 0 && (
                <div className="qd-info-item">
                  <span className="qd-info-label">全單加成</span>
                  <span className="qd-info-value">{quote.globalMarkup}%</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Section 2: Items */}
        <section className="qd-section">
          <div className="qd-section-header">
            <span className="qd-section-number">2</span>
            報價品項清單
            <span className="qd-item-count">（{quote.items?.length || 0} 項）</span>
          </div>
          <div className="qd-section-body">
            {(!quote.items || quote.items.length === 0) ? (
              <div className="qd-empty">尚無品項</div>
            ) : (
              <div className="qd-items-table-wrap">
                <table className="qd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>產品編號</th>
                      <th>中文品名</th>
                      <th>英文品名</th>
                      <th>規格/箱入</th>
                      <th>價格指數</th>
                      <th>MOQ</th>
                      <th>單價</th>
                      <th>數量</th>
                      <th>小計</th>
                      <th>葷素備註</th>
                      <th>業務備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.map((item, idx) => (
                      <tr key={item._id || idx}>
                        <td className="qd-row-num">{idx + 1}</td>
                        {/* 需求 R25：產品編號欄優先使用 snapshot 中的 productCode，
                                舊報價單未存時 fallback 到 populate 的 item.product.productCode。 */}
                        <td className="qd-mono">{item.productCode || item.product?.productCode || ''}</td>
                        <td>{item.productName}</td>
                        <td>{item.englishName}</td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.5 }}>
                            <span>
                              {item.spec}
                              {item.pcsPerBox && <span className="qd-pcs">/{item.pcsPerBox}入</span>}
                            </span>
                            {/* 需求 R25：國際條碼移至規格/箱入下方（PM 2026-05-18 確認）。
                                  詳細頁與編輯頁採同一版面：產品資訊欄顯示產品貨編，barcode 改於此欄顯示。
                                  舊報價單若 snapshot 未存 barcode，fallback 到 populate 的 item.product.barcode。 */}
                            {(item.barcode || item.product?.barcode) && (
                              <span className="qd-barcode">{item.barcode || item.product?.barcode}</span>
                            )}
                          </div>
                        </td>
                        <td>{item.priceTier}</td>
                        <td>{item.moq || '—'}</td>
                        <td className="qd-price">
                          {item.isManualPrice && <span className="qd-manual-tag" title="手動輸入">M</span>}
                          {fmtDetailMoney(item.unitPrice, quote.currency)}
                        </td>
                        <td>{item.quantity || 0}</td>
                        <td className="qd-price">
                          {/* 小計 = 已進位單價 × 數量，乘完再收一次浮點雜訊（1.23 × 3 === 3.6899999999999995）。
                              與匯出的箱價 calcBoxPrice（需求 R111 Q3=A）同一種寫法。 */}
                          {item.unitPrice != null
                            ? fmtDetailMoney(
                                (ceilMoney(item.unitPrice, quote.currency) ?? 0) * (item.quantity || 0),
                                quote.currency
                              )
                            : '—'}
                        </td>
                        <td>{item.vegetarianNote || '—'}</td>
                        {/* 需求 R96 R2（PM Q5 拍板：UI 已無 notes 欄位，舊 notes 值不再顯示）：
                            移除 `|| item.notes` 舊備援；此欄僅顯示 salesNotes，與匯出（Q5）一致。 */}
                        <td>{item.salesNotes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Transaction */}
        <section className="qd-section">
          <div className="qd-section-header">
            <span className="qd-section-number">3</span>
            交易細節與備註
          </div>
          <div className="qd-section-body">
            <div className="qd-info-grid qd-grid-1">
              {/* 需求 R143 Q3=A（PM 2026-08-29）：標籤由「交易條件備註」改為「交易條件」，
                  三支頁面（建立／編輯／詳情）用同一個名稱。 */}
              <div className="qd-info-item">
                <span className="qd-info-label">交易條件</span>
                <span className="qd-info-value qd-pre-wrap">{quote.transactionNotes || '—'}</span>
              </div>
              {/* 需求 R143 Q5=A：備註也要在詳情頁看得到（否則存了看不到）。
                  ⚠️ 這是「畫面」不是「匯出」——AC6-AC7 只限制 PDF／Excel，詳情頁顯示不違反。 */}
              <div className="qd-info-item">
                <span className="qd-info-label">備註</span>
                <span className="qd-info-value qd-pre-wrap">{quote.internalNotes || '—'}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">聯絡人 (業務)</span>
                <span className="qd-info-value">{quote.contactPerson?.name || '—'}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">建立者</span>
                <span className="qd-info-value">{quote.createdBy?.name || '—'}</span>
              </div>
              <div className="qd-info-item">
                <span className="qd-info-label">建立時間</span>
                <span className="qd-info-value">{formatDateTime(quote.createdAt)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom action bar */}
        <div className="qd-bottom-bar">
          {getActionButtons()}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="qd-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="qd-modal" onClick={e => e.stopPropagation()}>
            <div className="qd-modal-title">確認取消</div>
            <div className="qd-modal-body">確定要離開此報價單頁面嗎？</div>
            <div className="qd-modal-actions">
              <button className="qd-btn qd-btn-secondary" onClick={() => setShowCancelModal(false)}>
                留在此頁
              </button>
              <button className="qd-btn qd-btn-danger" onClick={confirmCancel}>
                確定離開
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetailPage;
